import formatErrorMessage from "../../../utils/formatErrorMessage.js";
import * as React from "react";
import { fastComponentRef } from "../../../utils/fastHooks.js";
import { useDialogRootContext } from "../root/DialogRootContext.js";
import { useButton } from "../../internals/use-button/useButton.js";
import { useRenderElement } from "../../internals/useRenderElement.js";
import { triggerOpenStateMapping } from "../../utils/popupStateMapping.js";
import { CLICK_TRIGGER_IDENTIFIER } from "../../internals/constants.js";
import { useBaseUiId } from "../../internals/useBaseUiId.js";
import { useOpenMethodTriggerProps } from "../../utils/useOpenInteractionType.js";
import { usePopupHandleStore } from "../../utils/popups/usePopupHandleStore.js";
import { useTriggerDataForwarding } from "../../utils/popups/popupStoreUtils.js";
import { useClick } from "../../floating-ui-react/hooks/useClick.js";
const DialogTrigger = fastComponentRef(function DialogTrigger2(componentProps, forwardedRef) {
  const {
    render,
    className,
    style,
    disabled = false,
    nativeButton = true,
    id: idProp,
    payload,
    handle,
    ...elementProps
  } = componentProps;
  const dialogRootStore = useDialogRootContext(true);
  const handleStore = usePopupHandleStore(handle);
  const store = handleStore ?? dialogRootStore;
  if (!store) {
    throw new Error(process.env.NODE_ENV !== "production" ? "Base UI: <Dialog.Trigger> must be used within <Dialog.Root> or provided with a handle." : formatErrorMessage(79));
  }
  const thisTriggerId = useBaseUiId(idProp);
  const floatingContext = store.useState("floatingRootContext");
  const isOpenedByThisTrigger = store.useState("isOpenedByTrigger", thisTriggerId);
  const popupId = store.useState("triggerPopupId", thisTriggerId);
  const triggerElementRef = React.useRef(null);
  const {
    registerTrigger,
    isMountedByThisTrigger
  } = useTriggerDataForwarding(thisTriggerId, triggerElementRef, store, {
    payload
  });
  const {
    getButtonProps,
    buttonRef
  } = useButton({
    disabled,
    native: nativeButton
  });
  const click = useClick(floatingContext);
  const interactionTypeProps = useOpenMethodTriggerProps(() => store.select("open"), (interactionType) => {
    store.set("openMethod", interactionType);
  });
  const state = {
    disabled,
    open: isOpenedByThisTrigger
  };
  const rootTriggerProps = store.useState("triggerProps", isMountedByThisTrigger);
  return useRenderElement("button", componentProps, {
    state,
    ref: [buttonRef, forwardedRef, registerTrigger, triggerElementRef],
    props: [click.reference, rootTriggerProps, interactionTypeProps, {
      [CLICK_TRIGGER_IDENTIFIER]: "",
      id: thisTriggerId,
      "aria-haspopup": "dialog",
      "aria-expanded": isOpenedByThisTrigger,
      "aria-controls": popupId
    }, elementProps, getButtonProps],
    stateAttributesMapping: triggerOpenStateMapping
  });
});
if (process.env.NODE_ENV !== "production") DialogTrigger.displayName = "DialogTrigger";
export {
  DialogTrigger
};
//# sourceMappingURL=DialogTrigger.js.map
